import React from 'react'
import clsx from 'clsx'
import { EMkLoadingType } from '@typescript'
import { useSiteContext } from '@contexts/MkSiteContext'
import { configs } from '@configs'
import { withBase } from '@utils/basePath'
import { Loader, LoaderProps } from './loaders/Loader'
import { PrimeDialog } from '../Dialog/PrimeDialog/PrimeDialog'

export const WM_LOADING_DEFAULTS = {
  imgSrc: '/content/images/wm/loading.gif',
  imgAlt: 'Loading...',
  heading: 'Destination: almost ready',
  subheading: 'Your content is loading...',
}

export const SIR_LOADING_DEFAULTS = {
  imgSrc: '/content/images/sir/loading.gif',
  imgAlt: 'Loading...',
  heading: 'Gearing Up',
  subheading: 'Your content is loading...',
}

export const CW_LOADING_DEFAULTS = {
  imgSrc: '/content/images/loadingAnimCWO.gif',
  imgAlt: 'Loading...',
  heading: 'Loading...',
  subheading: 'Please wait while we load the content.',
}

export const EBAC_LOADING_DEFAULTS = {
  imgSrc: '/content/images/ebac/loading.gif',
  imgAlt: 'Loading...',
  heading: 'Your Adventure Begins Soon',
  subheading: 'Content is loading...',
}

export const LOADING_PROPS = {
  wm: WM_LOADING_DEFAULTS,
  cw: CW_LOADING_DEFAULTS,
  sir: SIR_LOADING_DEFAULTS,
  ebac: EBAC_LOADING_DEFAULTS,
} as const

export type BrandType = keyof typeof LOADING_PROPS

/**
 * Type guard to check if a string is a valid BrandType.
 * Uses own-property check so inherited keys (e.g. "constructor") from env/storage are not treated as valid.
 * @param brand - The brand string to check
 * @returns True if the brand is a valid BrandType
 */
const isBrandType = (brand: string): brand is BrandType => Object.prototype.hasOwnProperty.call(LOADING_PROPS, brand)

/**
 * Get loading props for a specific brand.
 * When brand is unknown, falls back to SIR for backwards compatibility.
 * @param brand - The brand identifier (e.g. from useSiteContext or env MK_DEFAULT_THEME_BRAND)
 * @returns Loading props object for the specified brand
 */
export const getLoadingProps = (brand: string | undefined | null) => {
  const b = String(brand || '').toLowerCase()
  if (isBrandType(b)) return LOADING_PROPS[b]
  return LOADING_PROPS.sir
}

export interface LoadingProps extends LoaderProps {
  type?: EMkLoadingType
  imgSrc?: string
  imgAlt?: string
  heading?: string
  subheading?: string
  className?: string
  shouldSetDimensions?: boolean
}

/**
 * MkLoading component displays a popup with a loading animation when api is waiting to be resolved
 * @component
 * @param {LoadingProps} props - The properties for the MkLoading component
 * @param {string} props.imgSrc - Animated image path
 * @param {string} props.imgAlt - Alt text for image
 * @param {string} props.heading - Heading text for load screen
 * @param {string} props.subheading - Text under the heading
 */
export const Loading: React.FC<LoadingProps> = ({ type = EMkLoadingType.PAGE, shouldSetDimensions = true, imgSrc, imgAlt, heading, subheading, className }) => {
  const { themeBrand } = useSiteContext()
  // Use env default when outside site context (e.g. initial app load before MkSiteProvider mounts)
  const brandForLoader = themeBrand || configs.mk.defaultThemeBrand

  const [isPopupDisplayed, setIsPopupDisplayed] = React.useState(true)

  const hidePopup = () => {
    setIsPopupDisplayed(false)
  }

  const modalContent = () => {
    const brandProps = getLoadingProps(brandForLoader)

    const resolvedImgSrc = imgSrc ?? withBase(brandProps.imgSrc)

    return (
      <Loader
        imgSrc={resolvedImgSrc}
        imgAlt={imgAlt ?? brandProps.imgAlt}
        heading={heading ?? brandProps.heading}
        subheading={subheading ?? brandProps.subheading}
      />
    )
  }

  if (type !== EMkLoadingType.MODAL) {
    return (
      <div
        data-testid="mk-loader"
        className={clsx('mk-loading', className)}
        {...(shouldSetDimensions && { style: { width: '100%', height: '100vh' } })}>
        {modalContent()}
      </div>
    )
  }

  return (
    <PrimeDialog
      className={className}
      visible={isPopupDisplayed}
      {...(shouldSetDimensions && { style: { width: '50rem', height: '50rem' } })}
      onHide={hidePopup}
      content={modalContent()}
      pt={{
        root: () => ({
          className: 'mk-loader',
          'data-testid': 'mk-loader',
        }),
      }}
    />
  )
}
